import { HotelSchema } from '@airasia-common/libraries/validation/schemas';
import {
  all,
  cancel,
  fork,
  put,
  select,
  take,
  takeLatest,
  call,
} from 'redux-saga/effects';
import { get, post } from '@airasia-common/libraries/http/hotelRequest';
import Cookies from 'js-cookie';
import { isEmpty } from 'lodash';
import ssoClient from '@airasia-common/libraries/http/ssoClient';
import {
  HTTP_CODE,
  CHECKOUT_STATUS_CODE,
  EVENT_TYPE,
  X_AUTH_HEADER,
  REFRESH_TOKEN,
} from '@airasia-common/libraries/constants';
import config from '@airasia-common/libraries/config';
import memoryStorage from '@airasia-common/libraries/storage/memoryStorage';
import { toHotelAPILanguage } from '../../hotel/ApiParams';
import { hotelFilterDefault } from '../../ParamFilter';
import {
  HOTELS_LIST_MINIMUM_SIZE,
  COUNTRY_CODE_HEADER,
  HERO_SIZE,
  THUMBNAIL_SIZE,
} from '../../constants';
import {
  HOTEL_ID,
  HOTEL_LISTING_PAGE_ID,
  LOB_HOTEL,
  HOTEL_LISTING_PAGE,
  HOTEL_DETAIL_PAGE,
  GUEST_DETAIL_PAGE,
} from '../../redux/analytics/analytics.constants';
import { actionCreators as analyticsActionCreators } from '../../redux/analytics/analytics.meta';
import { actionCreators as smartfillActionCreators } from '../../redux/hotel/smartfill.meta';
import { actionCreators as fireBaseActionCreators } from '../../redux/firebaseAnalytics/firebaseAnalytic.meta';
import genericApiSaga from '../genericApiSaga';
import { actionCreators, types } from '../../redux/hotel/hotel.meta';
import {
  actionCreators as filterActionCreators,
  types as filterTypes,
} from '../../redux/filter/filter.meta';

import { actionCreators as userActionCreators } from '../../redux/user/user.meta';

import {
  getHeroImage,
  getUserTierFromStorage,
  getRefreshAuthToken,
} from '../../helpers';
import { fetchSmartfill } from '../../redux/hotel/smartfill.meta.saga';
import { mergeParams, getSubscriptionData } from './utils';

let Platform;
try {
  let RN = require('react-native');
  Platform = RN.Platform;
} catch (e) {
  if (e instanceof Error && e.code === 'MODULE_NOT_FOUND') {
    console.log('module not found!');
  }
}

const AA_TOKENS = 'aa_tokens';
const SSO_INFO_ITEM = 'SSO_INFO_ITEM';
const HOTEL_SEARCH_ENDPOINT_NAME = 'HotelSearch';
const HOTEL_DETAIL_ENDPOINT_NAME = 'HotelDetail';
const HOTEL_ROOM_ENDPOINT_NAME = 'RoomList';
const SUBSCRIPTION = 'subscription';
const HOTEL_IMPRESION_PAGE_LENGTH = 10;
export const getFeatureFlags = ({ metadata }) => metadata.featureFlags;
const getPageNumber = ({ hotel }) => hotel.pageNumber;
const getRefreshAccessToken = ({ hotel }) => hotel.refreshToken;
const getHotelFilter = ({ filter }) => filter.hotel;
const getHotels = ({ hotel }) => hotel.hotelList;
const getHotelDetail = ({ hotel }) => hotel.hotelDetail;
const getSsoUser = ({ user }) => user.ssoData;
const getSsoUserSubscription = ({ user }) => user.subscription;
const getUserTier = ({ user }) => user.ssoData?.loyaltyTier;
const getSmartfill = ({ smartfill }) => smartfill?.data;
const getLemonAgent = hotelFilter =>
  hotelFilter.lemonAgentId ? { lemon_agent_id: hotelFilter.lemonAgentId } : {};

const getAmenity = hotelFilter =>
  hotelFilter.amenity ? { amenity: hotelFilter.amenity } : {};

const getChannel = hotelFilter =>
  hotelFilter.channel ? { channel: hotelFilter.channel } : {};

const getSSOInfo = () => {
  if (typeof localStorage !== 'undefined') {
    const aaTokens = localStorage.getItem(AA_TOKENS);
    return aaTokens && JSON.parse(aaTokens);
  } else {
    return memoryStorage.getItem(SSO_INFO_ITEM);
  }
};

const getHotelCurrency = selectedCurrency => ({
  metadata: { hotelConfig },
}) => {
  if (hotelConfig.length === 0) {
    return {
      currency: hotelFilterDefault.currency,
      selectedCurrency,
    };
  }

  const { currency: supportedCurrencies = [] } =
    hotelConfig.find(({ currency }) => Boolean(currency)) || {};
  const isCurrencySupported = supportedCurrencies.includes(selectedCurrency);

  return {
    currency: isCurrencySupported
      ? selectedCurrency
      : hotelFilterDefault.currency,
    selectedCurrency,
  };
};

const getHotelLanguage = selectedLanguage => ({
  metadata: { hotelConfig },
}) => {
  if (hotelConfig.length === 0) {
    return hotelFilterDefault.languageId;
  }

  const { locale: supportedLanguages = [] } =
    hotelConfig.find(({ locale }) => Boolean(locale)) || {};
  const isLanguageSupported = supportedLanguages.includes(selectedLanguage);

  return isLanguageSupported ? selectedLanguage : hotelFilterDefault.languageId;
};

const hotelDiscardedReasons = {
  id: 'Hotel ID Missing',
  'prices.rate.lead_price.base_rate.value': 'Hotel Price Missing',
  headline: 'Hotel location Missing',
  name: 'Hotel name Missing',
  'gallery.images': 'Hero image URL Missing',
};

const sanitizeHotelsRequestResponse = (response, hotelFilter) => {
  const { status, body } = response;

  if (status !== 200) return response;

  const {
    aggregations,
    hotels,
    page_number,
    flyer_discount,
    default_locale_used,
    big_membership,
    stay_detail,
  } = body;

  const hotelsInList = [];
  const hotelsDiscarded = [];

  if (hotels) {
    hotels.forEach(hotel => {
      try {
        //HotelSchema.validateSync(hotel); As for no hotel available we are not getting prices and id. In that case we can not validate the respose structure.
        hotelsInList.push(hotel);
      } catch (err) {
        const errorPath = Object.keys(hotelDiscardedReasons).find(
          key => err.path.indexOf(key) === 0
        );
        if (errorPath) {
          hotelsDiscarded.push({
            ...hotel,
            reason: hotelDiscardedReasons[errorPath],
          });
        }
      }
    });
  }

  const {
    sortBy,
    priceFilter,
    paymentType,
    promotions,
    defaultFilter,
  } = hotelFilter;

  const filteredHotelInList =
    sortBy !== defaultFilter.sortBy ||
    priceFilter.length !== 0 ||
    !!paymentType.length ||
    !!promotions.length
      ? hotelsInList.filter(hotel => hotel.prices)
      : hotelsInList;

  return {
    status: status,
    body: {
      aggregations,
      hotels: filteredHotelInList,
      hotelsDiscarded,
      pageNumber: page_number,
      flyerDiscount: flyer_discount,
      defaultLocaleUsed: default_locale_used,
      bigMembership: big_membership,
      stay_detail,
    },
  };
};

function* getLocationName({ locationId, languageId }) {
  if (!locationId) {
    return '';
  }
  const smartfillData = yield select(getSmartfill);
  if (
    smartfillData &&
    smartfillData?.name &&
    smartfillData?.id === locationId
  ) {
    return smartfillData?.name;
  }
  const locationResponse = yield get(`/smartfill/v3/smartfill/${locationId}`, {
    language: toHotelAPILanguage(languageId),
  });
  if (locationResponse.status === 200) {
    return locationResponse.body.name;
  }
  return '';
}

function* getLocationInfo({ locationId, languageId }) {
  if (!locationId) {
    return '';
  }

  const smartfillData = yield select(getSmartfill);

  if (smartfillData && smartfillData?.id === locationId) {
    return smartfillData;
  }
  const locationResponse = yield get(`/smartfill/v3/smartfill/${locationId}`, {
    language: toHotelAPILanguage(languageId),
  });
  if (locationResponse.status === 200) {
    yield put(
      smartfillActionCreators.fetchSmartfillData(locationResponse.body)
    );
    return locationResponse.body;
  }
  return {};
}

function* sendDiscardedHotelsEvents({ hotelsDiscarded }) {
  const effects = hotelsDiscarded.map(({ id, name, reason }) =>
    put(
      actionCreators.trackHotelDiscarded({
        hotelId: id,
        hotelName: name,
        reason,
      })
    )
  );
  yield all(effects);
}

function* fetchHotelList({ payload }) {
  const hotelFilter = yield select(getHotelFilter);
  const pageNumber = yield select(getPageNumber);
  const requestStart = new Date().getTime();
  const { defaultFilter, sortBy, guestRating } = hotelFilter;
  // We just apply for default filter
  let aaId = '';
  if (
    defaultFilter &&
    sortBy === defaultFilter.sortBy &&
    guestRating === defaultFilter.guestRating
  ) {
    aaId = hotelFilter.aaId;
  }

  function* onResponse(response) {
    if (response.status) {
      yield put(
        analyticsActionCreators.trackStatusCode({
          statusCode: response.status,
          lob: HOTEL_ID,
          pageId: HOTEL_LISTING_PAGE_ID,
        })
      );

      const responseEnd = new Date().getTime();
      yield put(
        analyticsActionCreators.trackReceiveApiResponse({
          api: HOTEL_SEARCH_ENDPOINT_NAME,
          responseTime: responseEnd - requestStart,
          lob: LOB_HOTEL,
          pageId: HOTEL_LISTING_PAGE,
        })
      );

      const { hotels } = response.body;
      if (hotels && hotels.length > 0) {
        const heroImages = hotels
          .map(
            ({ gallery }) =>
              getHeroImage(gallery.images, THUMBNAIL_SIZE) ||
              getHeroImage(gallery.images, HERO_SIZE)
          )
          .filter(image => !!image);

        yield put(
          analyticsActionCreators.trackBatchContentDownload({
            items: heroImages,
            lob: LOB_HOTEL,
            pageId: HOTEL_LISTING_PAGE,
            isFirstBatch: !pageNumber || pageNumber === 0,
          })
        );
      }
    }
  }

  const xAuthHeader = {};
  if (payload) {
    xAuthHeader[X_AUTH_HEADER] = payload;
  }

  const lemonAgent = getLemonAgent(hotelFilter);

  yield genericApiSaga({
    onResponse: onResponse,
    completed: actionCreators.trackHotelListVisible,
    gatewayCall: () =>
      get(
        '/search/v2/hotels',
        {
          page_number: pageNumber,
          guest_score: hotelFilter.guestRating,
          currency: hotelFilter.currency,
          country_code: hotelFilter.countryCode,
          checkin: hotelFilter.checkin,
          checkout: hotelFilter.checkout,
          occupancy: hotelFilter.occupancy,
          location_id: hotelFilter.locationId,
          hotels_size: hotelFilter.hotelsSize,
          language: toHotelAPILanguage(hotelFilter.languageId),
          user_selected_hotel_id: aaId,
          ...lemonAgent,
        },
        xAuthHeader
      ).then(response => sanitizeHotelsRequestResponse(response, hotelFilter)),
    beforeCompleted: sendDiscardedHotelsEvents,
    failed: actionCreators.fetchHotelListFailed,
  });
}

function* fetchRefreshToken() {
  try {
    const ssoOrigin = yield ssoClient.authorize();
    const { clientId, apiKey } = ssoOrigin;
    // https://airasia.atlassian.net/wiki/spaces/AASET/pages/287408198/Authorization+by-refresh-token
    const refreshToken = yield getRefreshAuthToken();
    const newToken = yield call(
      ssoClient.refreshSSOToken,
      apiKey,
      clientId,
      refreshToken
    );
    yield put(
      actionCreators.fetchRefreshTokenSuccess({
        refreshToken: newToken,
      })
    );
  } catch (error) {
    console.error('Token refresh failed:', error);
  }
}

function* fetchHotelListV2({ payload }) {
  const {
    lemonAgentToken,
    accessToken,
    refreshToken: ssorefreshToken,
    loyaltyId,
    locationId = '',
    group,
    isWeb = false,
    payloadFilter = {},
  } = payload || {};
  const hotelFilter =
    typeof payloadFilter === 'object' && Object.keys(payloadFilter).length > 0
      ? payloadFilter
      : yield select(getHotelFilter);

  const pageNumber = yield select(getPageNumber);
  const userTierState = yield select(getUserTier);
  const refreshToken = yield select(getRefreshAccessToken);
  const userTierStorage = yield getUserTierFromStorage();
  const userSubscription = yield select(getSsoUserSubscription);
  const featureFlags = yield select(getFeatureFlags);
  const requestStart = new Date().getTime();
  const xAuthHeader = {};
  const { defaultFilter, sortBy } = hotelFilter;
  // We just apply for default
  let aaId = '';
  if (defaultFilter && sortBy === defaultFilter.sortBy) {
    aaId = hotelFilter.aaId;
  }

  const lemonAgent = getLemonAgent(hotelFilter);
  const channel = getChannel(hotelFilter);
  let url = '/bff/search/v2/hotels';

  if (lemonAgentToken) {
    xAuthHeader[X_AUTH_HEADER] = lemonAgentToken;
    xAuthHeader[REFRESH_TOKEN] = ssorefreshToken;
  } else if (accessToken) {
    if (!isWeb) {
      xAuthHeader[X_AUTH_HEADER] = accessToken;
    } else {
      xAuthHeader[X_AUTH_HEADER] = refreshToken || accessToken;
    }
    if (featureFlags?.SEND_REFRESH_TOKEN_IN_BFFAPIS) {
      xAuthHeader[REFRESH_TOKEN] = ssorefreshToken;
    }
  }

  const amenity = getAmenity(hotelFilter);
  const enableUserSubscription = isWeb
    ? featureFlags.ENABLE_USER_SUBSCRIPTION_WEB
    : featureFlags.ENABLE_USER_SUBSCRIPTION_APP;

  const getSubscription = isWeb
    ? localStorage.getItem(SUBSCRIPTION)
    : undefined;

  let hotelLocationId = hotelFilter.locationId;

  if (typeof locationId === 'string' && locationId) {
    hotelLocationId = locationId;
  }
  function* onResponse(response) {
    if (response.status) {
      const responseEnd = new Date().getTime();
      if (Platform && (Platform.OS === 'android' || Platform.OS === 'ios')) {
        yield put(
          fireBaseActionCreators.trackStatusCode({
            statusCode: response.status,
            lob: HOTEL_ID,
            pageId: HOTEL_LISTING_PAGE_ID,
          })
        );

        yield put(
          fireBaseActionCreators.trackReceiveApiResponse({
            category: `${HOTEL_ID} ${HOTEL_LISTING_PAGE_ID} page performance event`,
            action: `${HOTEL_SEARCH_ENDPOINT_NAME} API Response Received`,
            label: `${responseEnd - requestStart} ms`,
            screenName: `${HOTEL_LISTING_PAGE_ID}`,
            lineofbusiness: HOTEL_ID,
          })
        );
      } else {
        yield put(
          analyticsActionCreators.trackStatusCode({
            statusCode: response.status,
            lob: HOTEL_ID,
            pageId: HOTEL_LISTING_PAGE_ID,
          })
        );

        yield put(
          analyticsActionCreators.trackReceiveApiResponse({
            api: HOTEL_SEARCH_ENDPOINT_NAME,
            responseTime: responseEnd - requestStart,
            lob: LOB_HOTEL,
            pageId: HOTEL_LISTING_PAGE,
          })
        );
      }

      const { hotels, stay_detail } = response.body;
      if (hotels && hotels.length > 0) {
        const heroImages = hotels
          .map(
            ({ gallery }) =>
              getHeroImage(gallery.images, THUMBNAIL_SIZE) ||
              getHeroImage(gallery.images, HERO_SIZE)
          )
          .filter(image => !!image);

        yield put(
          analyticsActionCreators.trackBatchContentDownload({
            items: heroImages,
            lob: LOB_HOTEL,
            pageId: HOTEL_LISTING_PAGE,
            isFirstBatch: !pageNumber || pageNumber === 0,
          })
        );
      }

      // need to put this temporarily but this code is not needed anymore in mobile
      // since we are already need to sync in stayDetail store
      if (isWeb && stay_detail?.checkin_date && stay_detail?.checkout_date) {
        yield put(
          filterActionCreators.updateHotelFilterCompleted({
            ...hotelFilter,
            checkin: stay_detail?.checkin_date,
            checkout: stay_detail?.checkout_date,
          })
        );
      }
    }
  }

  const hotelSize =
    featureFlags.HOTEL_LIST_BATCH_SIZE || hotelFilter.hotelsSize;

  const defaultParams = {
    page_number: pageNumber,
    guest_score: hotelFilter.guestRating,
    currency: hotelFilter.currency,
    country_code: hotelFilter.countryCode,
    checkin: hotelFilter.checkin,
    checkout: hotelFilter.checkout,
    occupancy: hotelFilter.occupancy,
    location_id: hotelLocationId,
    hotels_size: hotelSize,
    language: toHotelAPILanguage(hotelFilter.languageId),
    mtype: userTierState || userTierStorage,
    user_selected_hotel_id: aaId,
    big_member_id: loyaltyId,
    star_rating: hotelFilter.hotelClass,
    payment_method: hotelFilter.paymentType,
    debug: hotelFilter.debug,
    category: hotelFilter.propertyType,
    group: group,
    ...lemonAgent,
    ...channel,
    ...amenity,
  };

  let params = { ...defaultParams };

  if (enableUserSubscription) {
    const subscriptionData = userSubscription?.data
      ? getSubscriptionData(JSON.stringify(userSubscription))
      : {};
    params = mergeParams(defaultParams, subscriptionData);
  }

  if (Object.keys(params).length) {
    yield genericApiSaga({
      onResponse: onResponse,
      completed: actionCreators.trackHotelListVisible,
      gatewayCall: () =>
        get(url, params, xAuthHeader).then(response =>
          sanitizeHotelsRequestResponse(response, hotelFilter)
        ),
      beforeCompleted: sendDiscardedHotelsEvents,
      failed: actionCreators.fetchHotelListFailed,
    });
  }
}

function* trackHotelListVisible({ payload }) {
  const filter = yield select(getHotelFilter);
  const { length = 0 } = yield select(getHotels);
  yield put(
    actionCreators.fetchHotelListCompleted({
      ...payload,
      sortBy: filter.sortBy,
      focusId: filter.focusId,
    })
  );

  const { hotels } = payload;
  const hasNoHotels = !hotels || hotels.length === 0;
  const hotelIds = hotels.map(hotel => hotel.id);

  // https://airasia.atlassian.net/browse/HOTEL-9, The logic relates to Scenario 3 in this story
  if (!hasNoHotels && length + hotels.length < HOTELS_LIST_MINIMUM_SIZE) {
    const SSOInfo = getSSOInfo();
    const hotelFilter = yield select(getHotelFilter);
    const lemonAgent = getLemonAgent(hotelFilter);
    const lemonAgentToken = Cookies.get('lemonAgent');
    if (SSOInfo && isEmpty(lemonAgent)) {
      const { accessToken, loyaltyId } = SSOInfo;
      yield put(
        actionCreators.fetchHotelListV2({
          accessToken,
          lemonAgentToken,
          loyaltyId,
        })
      );
    } else {
      if (lemonAgentToken && lemonAgent) {
        yield put(
          actionCreators.fetchHotelListV2({ ...payload, lemonAgentToken })
        );
      } else {
        yield put(actionCreators.fetchHotelListV2(payload));
      }
    }
  }

  const currentHotels = yield select(getHotels);
  const ssoUser = yield select(getSsoUser);
  const latestHotels = currentHotels.filter(hotel =>
    hotelIds.includes(hotel.id)
  );

  const startIndex =
    payload.pageNumber === 0 ? 0 : currentHotels.length - latestHotels.length;

  yield trackHotelListImpressions(latestHotels, filter, ssoUser, startIndex);
}

function* trackHotelListImpressions(hotels, filter, ssoUser, startIndex = 0) {
  const totalIterations = Math.ceil(
    hotels.length / HOTEL_IMPRESION_PAGE_LENGTH
  );
  let startSlice = 0;
  let endSlice = HOTEL_IMPRESION_PAGE_LENGTH;

  for (let i = 0; i < totalIterations; i++) {
    const hotelSlice = hotels.slice(startSlice, endSlice);
    startSlice = endSlice;
    endSlice += HOTEL_IMPRESION_PAGE_LENGTH;
    yield put(
      actionCreators.hotelListImpressions({
        hotels: hotelSlice,
        filter,
        isLoggedInUser: Boolean(ssoUser),
        startIndex: startIndex + i * HOTEL_IMPRESION_PAGE_LENGTH,
      })
    );
  }
}

function* trackHotelDetail(result) {
  const SSOInfo = getSSOInfo();
  const filter = yield select(getHotelFilter);
  const ssoUser = yield select(getSsoUser);
  yield put(
    actionCreators.hotelDetailImpression({
      result,
      filter,
      isLoggedInUser: Boolean(SSOInfo && SSOInfo.userId),
    })
  );
}

function* sendFilterAnalyticsEvents(payload) {
  const hotelFilter = yield select(getHotelFilter);

  const { sortBy, guestRating } = payload || {};
}

function* updateFilterAndTracking({ payload }) {
  yield sendFilterAnalyticsEvents(payload);
  yield updateFilter(payload);
}

function* clearFilter() {
  // Reset filter with empty payload
  yield updateFilter();
}

function* updateFilter(payload) {
  if (payload && payload.needToFetchLocationInfo) {
    const locationInfo = yield getLocationInfo(payload);
    const {
      name: location,
      type: locationType,
      country_code: locationCountryCode,
      coordinates,
    } = locationInfo;

    let { nearby_poi: nearbyPoi = [], neighborhood = [] } = locationInfo;

    if (nearbyPoi && nearbyPoi.length > 0) {
      nearbyPoi = nearbyPoi.map(e => {
        return {
          id: e.id,
          text: e.name + ` (${e.hotels_count})`,
        };
      });
    }
    if (neighborhood && neighborhood.length > 0) {
      neighborhood = neighborhood.map(e => {
        return {
          id: e.id,
          text: e.name + ` (${e.hotels_count})`,
        };
      });
    }

    yield put(
      filterActionCreators.updateHotelFilterCompleted({
        ...payload,
        nearbyPoi,
        location,
        locationType,
        locationCountryCode,
        coordinates,
        neighborhood,
      })
    );
  } else {
    yield put(filterActionCreators.updateHotelFilterCompleted(payload));
  }

  const filter = yield select(getHotelFilter);
  if (filter.updateAtClient) {
    yield put(actionCreators.sortHotelList(filter));
    const hotels = yield select(getHotels);
    const ssoUser = yield select(getSsoUser);
    yield trackHotelListImpressions(hotels, filter, ssoUser);
  } else {
    yield put(actionCreators.clearHotelList());
  }
}

function* fetchHotelDetail(action) {
  const hotelId = action.payload;
  const requestStart = new Date().getTime();
  const hotelFilter = yield select(getHotelFilter);
  const lemonAgent = getLemonAgent(hotelFilter);

  function* onResponse(response) {
    if (response.status) {
      const responseEnd = new Date().getTime();
      if (Platform && (Platform.OS === 'android' || Platform.OS === 'ios')) {
        yield put(
          fireBaseActionCreators.trackStatusCode({
            statusCode: response.status,
            lob: HOTEL_ID,
            pageId: HOTEL_DETAIL_PAGE,
          })
        );

        yield put(
          fireBaseActionCreators.trackReceiveApiResponse({
            category: `${LOB_HOTEL} ${HOTEL_DETAIL_PAGE} page performance event`,
            action: `${HOTEL_SEARCH_ENDPOINT_NAME} API Response Received`,
            label: `${responseEnd - requestStart} ms`,
            screenName: `${HOTEL_DETAIL_PAGE}`,
            lineofbusiness: `${LOB_HOTEL}`,
          })
        );
      } else {
        yield put(
          analyticsActionCreators.trackStatusCode({
            statusCode: response.status,
            lob: HOTEL_ID,
            pageId: HOTEL_DETAIL_PAGE,
          })
        );
        yield put(
          analyticsActionCreators.trackReceiveApiResponse({
            api: HOTEL_DETAIL_ENDPOINT_NAME,
            responseTime: responseEnd - requestStart,
            lob: LOB_HOTEL,
            pageId: HOTEL_DETAIL_PAGE,
          })
        );
      }
    }
  }

  yield genericApiSaga({
    completed: actionCreators.fetchHotelDetailCompleted,
    failed: actionCreators.fetchHotelDetailFailed,
    gatewayCall: () =>
      get(`/details/v2/hotels/${hotelId}/content`, {
        language: toHotelAPILanguage(hotelFilter.languageId),
        ...lemonAgent,
      }),
    onResponse,
  });
}

function* fetchHotelRooms({ payload }) {
  const hotelFilter = yield select(getHotelFilter);
  const userTierState = yield select(getUserTier);
  const userTierStorage = yield getUserTierFromStorage();
  const userSubscription = yield select(getSsoUserSubscription);
  const refreshToken = yield select(getRefreshAccessToken);
  const featureFlags = yield select(getFeatureFlags);
  const requestStart = Date.now();
  const {
    hotelId,
    accessToken,
    ssorefreshToken,
    lemonAgentToken,
    loyaltyId,
    isWeb = false,
    group,
    mft = '',
  } = payload;
  let url = `/bff/details/v2/hotels/${hotelId}/rooms`;
  const xAuthHeader = {};
  if (lemonAgentToken) {
    xAuthHeader[X_AUTH_HEADER] = lemonAgentToken;
    xAuthHeader[REFRESH_TOKEN] = ssorefreshToken;
  } else if (accessToken) {
    if (!isWeb) {
      xAuthHeader[X_AUTH_HEADER] = accessToken;
    } else {
      xAuthHeader[X_AUTH_HEADER] = refreshToken || accessToken;
    }
    if (featureFlags?.SEND_REFRESH_TOKEN_IN_BFFAPIS) {
      xAuthHeader[REFRESH_TOKEN] = ssorefreshToken;
    }
  }
  const lemonAgent = getLemonAgent(hotelFilter);
  const channel = getChannel(hotelFilter);
  function* onResponse(response) {
    if (response.status) {
      const responseEnd = new Date().getTime();
      yield put(
        analyticsActionCreators.trackReceiveApiResponse({
          api: HOTEL_ROOM_ENDPOINT_NAME,
          responseTime: responseEnd - requestStart,
          lob: LOB_HOTEL,
          pageId: HOTEL_DETAIL_PAGE,
        })
      );

      const { rooms, stay_detail } = response.body;
      if (rooms && rooms.length > 0) {
        const heroImages = rooms
          .map(({ gallery }) => {
            if (gallery)
              return (
                getHeroImage(gallery.images, THUMBNAIL_SIZE) ||
                getHeroImage(gallery.images, HERO_SIZE)
              );

            return false;
          })
          .filter(image => !!image);

        yield put(
          analyticsActionCreators.trackBatchContentDownload({
            items: heroImages,
            lob: LOB_HOTEL,
            pageId: HOTEL_DETAIL_PAGE,
            isFirstBatch: true,
          })
        );
      }

      if (stay_detail.checkin_date && stay_detail.checkout_date) {
        yield put(
          filterActionCreators.updateHotelFilterCompleted({
            ...hotelFilter,
            checkin: stay_detail.checkin_date,
            checkout: stay_detail.checkout_date,
          })
        );
      }
    }
  }
  const enableUserSubscription = isWeb
    ? featureFlags.ENABLE_USER_SUBSCRIPTION_WEB
    : featureFlags.ENABLE_USER_SUBSCRIPTION_APP;

  const defaultParams = {
    currency: hotelFilter.currency,
    country_code: hotelFilter.countryCode,
    checkin: hotelFilter.checkin,
    checkout: hotelFilter.checkout,
    occupancy: hotelFilter.occupancy,
    language: toHotelAPILanguage(hotelFilter.languageId),
    mtype: userTierState || userTierStorage,
    big_member_id: loyaltyId,
    debug: hotelFilter.debug,
    group: group,
    ...(mft && { mft }),
    ...lemonAgent,
    ...channel,
  };

  let params = { ...defaultParams };

  if (enableUserSubscription) {
    const subscriptionData = userSubscription?.data
      ? getSubscriptionData(JSON.stringify(userSubscription))
      : {};
    params = mergeParams(defaultParams, subscriptionData);
  }

  if (Object.keys(params).length) {
    yield genericApiSaga({
      completed: payload =>
        actionCreators.trackVisibleHotelRooms({
          ...payload,
          checkout: hotelFilter.checkout,
        }),
      failed: actionCreators.fetchHotelRoomsFailed,
      gatewayCall: () => get(url, params, xAuthHeader),
      onResponse,
    });
  }
}

function* trackVisibleHotelRooms({ payload }) {
  yield put(actionCreators.fetchHotelRoomsCompleted(payload));

  const hotelDetail = yield select(getHotelDetail);
  yield put(actionCreators.hotelRoomsImpressions(hotelDetail));
}

export const fetchIP = async urlList => {
  const endpoints = urlList || [
    'https://ipapia.co/json/',
    'https://api.ipify.org?format=json',
    'https://ipinfo.io/json',
  ];

  const TIMEOUT_MS = 5000;

  // Helper function to validate IP
  const validateIP = data => {
    if (!data || typeof data.ip !== 'string' || data.ip.trim() === '') {
      return null;
    }
    return data.ip;
  };

  // Helper function to fetch with timeout using Promise.race
  const fetchWithTimeout = async url => {
    const fetchPromise = fetch(url);
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(
        () =>
          reject(
            new Error(`Request to ${url} timed out after ${TIMEOUT_MS}ms`)
          ),
        TIMEOUT_MS
      );
    });

    return Promise.race([fetchPromise, timeoutPromise]);
  };

  // If primary fails, try fallbacks in parallel
  try {
    // Use Promise.any to get the first successful result
    const fetchPromises = endpoints.map(endpoint =>
      fetchWithTimeout(endpoint)
        .then(response => {
          if (!response.ok)
            throw new Error(
              `Endpoint ${endpoint} failed with status ${response.status}`
            );
          return response.json();
        })
        .then(data => {
          const ip = validateIP(data);
          if (ip) {
            return ip;
          }
          throw new Error(`Invalid IP data from ${endpoint}`);
        })
        .catch(error => {
          console.error(`Error fetching from ${endpoint}:`, error);
          throw error;
        })
    );

    // Promise.any returns the first promise that fulfills
    const ip = await Promise.any(fetchPromises);

    return ip;
  } catch (error) {
    // AggregateError is thrown when all promises fail
    if (error.name === 'AggregateError') {
      console.error('All IP fetch attempts failed');
    } else {
      console.error('Unexpected error in fetchIP:', error);
    }
    return null;
  }
};

function* checkoutHotelBooking(action) {
  const { filter: hotelFilter, ...restParam } = action.payload;
  const requestParam = {
    ...restParam,
    checkin_date: hotelFilter.checkin,
    checkout_date: hotelFilter.checkout,
    occupancy_per_room: [
      ...hotelFilter.occupancy.map(guest => guest.toString()),
    ],
    user_locale: toHotelAPILanguage(hotelFilter.languageId),
  };

  const isApiAddressEnabled = Boolean(requestParam?.fetchApi);
  delete requestParam?.fetchApi;

  try {
    const ipAddress = isApiAddressEnabled ? yield call(fetchIP) : null;

    const headers = isApiAddressEnabled
      ? { 'customer-ip': ipAddress || '' }
      : undefined;

    const response = yield call(() =>
      post('/hotel-checkout/v1/checkout', requestParam, headers)
    );

    switch (response.status) {
      case CHECKOUT_STATUS_CODE.PRICE_CHANGED:
      case CHECKOUT_STATUS_CODE.HOTEL_SOLD_OUT:
      case HTTP_CODE.OK:
        yield put(
          actionCreators.checkoutHotelBookingCompleted({
            status: response.status,
            data: response.body,
          })
        );
        break;
      default:
        yield put(actionCreators.checkoutHotelBookingFailed());
    }
  } catch (error) {
    yield put(actionCreators.checkoutHotelBookingFailed());
  }
}

function* createHotelCheckoutSaga() {
  while (true) {
    const action = yield take(types.CHECKOUT_HOTEL_BOOKING);
    const checkoutTask = yield fork(checkoutHotelBooking, action);
    const checkoutEffects = yield take([
      types.CANCEL_CHECKOUT_HOTEL_BOOKING,
      types.CHECKOUT_HOTEL_BOOKING_FAILED,
    ]);
    if (checkoutEffects.type === types.CANCEL_CHECKOUT_HOTEL_BOOKING) {
      yield cancel(checkoutTask);
    }
  }
}

function* initHotelFilterV2({ payload }) {
  const { country_code: superAppCountryCode } = payload;
  let headerCountryCode = 'MY';

  const { currency, selectedCurrency } = yield select(
    getHotelCurrency(payload.currency)
  );
  const languageId = yield select(getHotelLanguage(payload.languageId));

  yield put(
    filterActionCreators.initHotelFilterCompleted({
      ...payload,
      countryCode: superAppCountryCode
        ? superAppCountryCode
        : headerCountryCode,
      currency,
      selectedCurrency: {
        value: selectedCurrency,
      },
      languageId,
    })
  );
}

function* initHotelFilter({ payload }) {
  const { country_code: superAppCountryCode } = payload;
  let headerCountryCode = 'MY';
  if (
    typeof sessionStorage !== 'undefined' &&
    sessionStorage?.getItem('geocountry')
  ) {
    const geoId = sessionStorage?.getItem('geocountry');
    headerCountryCode = geoId !== 'undefined' ? geoId : 'MY';
  } else if (!superAppCountryCode || superAppCountryCode === 'undefined') {
    headerCountryCode = yield fetch(config.REACT_APP_COUNTRY_CODE_ENDPOINT, {
      method: 'HEAD',
    })
      .then(
        response =>
          response.headers.get(COUNTRY_CODE_HEADER) ||
          hotelFilterDefault.countryCode
      )
      .catch(() => hotelFilterDefault.countryCode);
  }
  const { currency, selectedCurrency } = yield select(
    getHotelCurrency(payload.currency)
  );
  const languageId = yield select(getHotelLanguage(payload.languageId));
  const locationInfo = yield getLocationInfo(payload);

  const {
    name: location,
    type: locationType,
    country_code: locationCountryCode,
    coordinates,
  } = locationInfo;
  let { nearby_poi: nearbyPoi = [], neighborhood = [] } = locationInfo;
  if (nearbyPoi && nearbyPoi.length > 0) {
    nearbyPoi = nearbyPoi.map(e => {
      return {
        id: e.id,
        text: e.name + ` (${e.hotels_count})`,
      };
    });
  }

  if (neighborhood && neighborhood.length > 0) {
    neighborhood = neighborhood.map(e => {
      return {
        id: e.id,
        text: e.name + ` (${e.hotels_count})`,
      };
    });
  }

  yield put(
    filterActionCreators.initHotelFilterCompleted({
      ...payload,
      countryCode: superAppCountryCode
        ? superAppCountryCode
        : headerCountryCode,
      location,
      locationType,
      locationCountryCode,
      currency,
      selectedCurrency: {
        value: selectedCurrency,
      },
      languageId,
      nearbyPoi,
      coordinates,
      neighborhood,
    })
  );
}

function* updateHotelCurrency({ payload }) {
  const { currency, selectedCurrency } = yield select(
    getHotelCurrency(payload.currency)
  );

  yield put(
    filterActionCreators.updateHotelFilterCompleted({
      currency,
      priceFilter: [],
      selectedCurrency: {
        value: selectedCurrency,
      },
    })
  );

  yield put(actionCreators.clearHotelList());
}

function* trackCompletedEvents(events, saga) {
  while (true) {
    const effects = events.map(event => take(event));
    const result = yield all(effects);
    yield fork(saga, result);
  }
}

function* fetchLocationNameFromId(action) {
  const location = yield getLocationName(action.payload);

  yield put(
    filterActionCreators.updateHotelFilterCompleted({
      location,
    })
  );
}

function* fetchSpecialRequests(action) {
  const requestStart = new Date().getTime();
  function* onResponse(response) {
    if (response.status) {
      const responseEnd = new Date().getTime();
      yield put(
        analyticsActionCreators.trackReceiveApiResponse({
          api: 'Special Requests',
          responseTime: responseEnd - requestStart,
          lob: LOB_HOTEL,
          pageId: GUEST_DETAIL_PAGE,
        })
      );
    }
  }

  const hotelId = action.payload;
  yield genericApiSaga({
    onResponse: onResponse,
    completed: actionCreators.fetchSpecialRequestsCompleted,
    gatewayCall: () => get(`/details/v2/hotels/special_request/${hotelId}`),
  });
}

function* fetchPreCheckout(action) {
  const requestStart = new Date().getTime();
  function* onResponse(response) {
    if (response.status) {
      const responseEnd = new Date().getTime();
      yield put(
        analyticsActionCreators.trackReceiveApiResponse({
          api: 'Pre Checkout',
          responseTime: responseEnd - requestStart,
          lob: LOB_HOTEL,
          pageId: GUEST_DETAIL_PAGE,
        })
      );
    }
  }

  const { roomId, hotelId, token, lang } = action.payload;
  yield genericApiSaga({
    onResponse: onResponse,
    completed: actionCreators.fetchPreCheckoutCompleted,
    gatewayCall: () =>
      post(
        `/hotel-checkout/v2/hotels/${hotelId}/rooms/${roomId}/pre_checkout`,
        {
          token: token,
          language: lang,
        }
      ),
    failed: actionCreators.fetchPreCheckoutFailed,
  });
}

function* fetchGuestReview(action) {
  const requestStart = new Date().getTime();
  function* onResponse(response) {
    if (response.status) {
      const responseEnd = new Date().getTime();
      yield put(
        analyticsActionCreators.trackReceiveApiResponse({
          api: 'User Review',
          responseTime: responseEnd - requestStart,
          lob: LOB_HOTEL,
          pageId: HOTEL_DETAIL_PAGE,
        })
      );
    }
  }

  const { hotelId, lang } = action.payload;
  yield genericApiSaga({
    onResponse: onResponse,
    completed: actionCreators.fetchGuestReviewCompleted,
    gatewayCall: () =>
      get(`/review/v1/hotels/reviews?hotel_id=${hotelId}&language=${lang}`),
    failed: actionCreators.fetchGuestReviewFailed,
  });
}
function* createHotelSaga() {
  yield takeLatest(types.FETCH_HOTEL_LIST, fetchHotelList);
  yield takeLatest(types.FETCH_HOTEL_LIST_V2, fetchHotelListV2);
  yield takeLatest(types.HOTEL_LIST_VISIBLE, trackHotelListVisible);
  yield takeLatest(filterTypes.UPDATE_HOTEL_FILTER, updateFilterAndTracking);
  yield takeLatest(filterTypes.CLEAR_HOTEL_FILTER, clearFilter);
  yield takeLatest(filterTypes.INIT_HOTEL_FILTER, initHotelFilter);
  yield takeLatest(filterTypes.INIT_HOTEL_FILTER_V2, initHotelFilterV2);
  yield takeLatest(filterTypes.UPDATE_HOTEL_CURRENCY, updateHotelCurrency);
  yield takeLatest(types.FETCH_HOTEL_DETAIL, fetchHotelDetail);
  yield takeLatest(types.FETCH_HOTEL_ROOMS, fetchHotelRooms);
  yield takeLatest(types.HOTEL_ROOM_VISIBLE, trackVisibleHotelRooms);
  yield takeLatest(types.FETCH_LOCATION_NAME_FROM_ID, fetchLocationNameFromId);
  yield takeLatest(types.FETCH_HOTEL_PRE_CHECKOUT, fetchPreCheckout);
  yield takeLatest(types.FETCH_SPECIAL_REQUEST, fetchSpecialRequests);
  yield takeLatest(types.FETCH_HOTEL_GUEST_REVIEW, fetchGuestReview);
  yield takeLatest(types.FETCH_HOTEL_REFRESH_TOKEN, fetchRefreshToken);
  yield trackCompletedEvents(
    [types.FETCH_HOTEL_DETAIL_COMPLETED, types.FETCH_HOTEL_ROOMS_COMPLETED],
    trackHotelDetail
  );
}

export { createHotelSaga, fetchHotelList, createHotelCheckoutSaga };
